import { Flex, Grid, hooks, Icon, Tooltip } from '@bitrise/bitkit';
import React, { FunctionComponent, useContext, useEffect } from 'react';
import { ATTRIBUTE_NAME_MAP } from '../../../constants/Attributes';
import dataAtIds from '../../../dataAtIds.json';
import { trackEvent } from '../../../utils/tracking';
import Filters from '../../Filters/Filters';
import FiltersSelector from '../../Filters/FiltersSelector';
import Page from '../../Page/Page';
import PageBody from '../../Page/PageBody';
import PageHeader from '../../Page/PageHeader';
import PageTitle from '../../Page/PageTitle';
import TimeRange from '../../TimeRange/TimeRange';
import { URLStateContext } from '../../URLState/URLState';
import PercentileLineChart from '../../ViewVisualisations/Percentile/LineChart/LineChart';
import PercentileMinMaxChart from '../../ViewVisualisations/Percentile/MinMaxChart/MinMaxChart';
import PercentileStackedBarChart from '../../ViewVisualisations/Percentile/StackedBarChart/StackedBarChart';
import usePercentileMetric from '../usePercentileMetric';
import PerformanceOverviewCard from './PerformanceOverviewCard';

const { useMediaQuery } = hooks;

const Overview: FunctionComponent = () => {
  const { attributeGroups } = useContext(URLStateContext);
  const match = useMediaQuery(['800px', '1200px']);

  useEffect(() => {
    trackEvent('PerformanceOverview.Visit', {});
  }, []);

  const metricApplicationCrash = usePercentileMetric('ApplicationCrash');
  const metricApplicationError = usePercentileMetric('ApplicationError');
  const metricNetworkError = usePercentileMetric('NetworkError');
  const metricStartupLatencyCold = usePercentileMetric('StartupLatencyCold');
  const metricStartupLatencyHot = usePercentileMetric('StartupLatencyHot');
  const metricNetworkRequestLatency = usePercentileMetric('NetworkRequestLatency');
  const metricNetworkRequestTotal = usePercentileMetric('NetworkRequestTotal');
  const metricCpuPct = usePercentileMetric('CpuPct');
  const metricMemoryBytes = usePercentileMetric('MemoryBytes');

  return (
    <Page data-trace-at-id={dataAtIds.PerformanceOverviewPage}>
      <PageHeader>
        <PageTitle>
          <Flex alignChildrenVertical="middle" direction="horizontal" gap="x2">
            <Flex>App Performance</Flex>

            <Flex>
              <Tooltip
                maxWidth="16rem"
                title="An overview of your app’s performance in the
                    critical areas of Stability, Responsiveness and Consumption,
                    broken down by a variety of device attributes and demographics
                    for a selected time period."
              >
                {({ ref, ...rest }) => (
                  <Icon {...rest} innerRef={ref} name="Info" textColor="gray-5" />
                )}
              </Tooltip>
            </Flex>
          </Flex>
        </PageTitle>

        <Flex alignChildrenHorizontal="between" direction="horizontal" gap="x4" margin="x5">
          <Flex grow>
            <FiltersSelector />
          </Flex>

          <Flex>
            <TimeRange />
          </Flex>
        </Flex>

        <Filters />
      </PageHeader>

      <PageBody>
        <Grid
          gap="x8"
          margin="x8"
          repeat={
            match({
              '800px': 2,
              '1200px': 3,
            }) || 1
          }
          repeatWidth="1fr"
        >
          <PerformanceOverviewCard
            description={`How often your app is generating fatal errors and
                crashing, broken down by '${
                  ATTRIBUTE_NAME_MAP[attributeGroups.ApplicationCrash]
                }'.`}
            disabled={metricApplicationCrash.isEmpty}
            metric="ApplicationCrash"
          >
            <PercentileStackedBarChart
              metrics={metricApplicationCrash.metrics}
              isFetched={metricApplicationCrash.isFetched}
              isFetching={metricApplicationCrash.isFetching}
              isError={metricApplicationCrash.isError}
              refetch={metricApplicationCrash.refetch}
              height={160}
              metric="ApplicationCrash"
            />
          </PerformanceOverviewCard>

          <PerformanceOverviewCard
            description={`How often your app is generating non-fatal errors,
                broken down by '${ATTRIBUTE_NAME_MAP[attributeGroups.ApplicationError]}'.`}
            disabled={metricApplicationError.isEmpty}
            metric="ApplicationError"
          >
            <PercentileStackedBarChart
              metrics={metricApplicationError.metrics}
              isFetched={metricApplicationError.isFetched}
              isFetching={metricApplicationError.isFetching}
              isError={metricApplicationError.isError}
              refetch={metricApplicationError.refetch}
              height={160}
              metric="ApplicationError"
            />
          </PerformanceOverviewCard>

          <PerformanceOverviewCard
            description={`How many requests are failing (non-2xx status codes),
                broken down by '${ATTRIBUTE_NAME_MAP[attributeGroups.NetworkError]}'`}
            disabled={metricNetworkError.isEmpty}
            metric="NetworkError"
          >
            <PercentileStackedBarChart
              metrics={metricNetworkError.metrics}
              isFetched={metricNetworkError.isFetched}
              isFetching={metricNetworkError.isFetching}
              isError={metricNetworkError.isError}
              refetch={metricNetworkError.refetch}
              height={160}
              metric="NetworkError"
            />
          </PerformanceOverviewCard>

          <PerformanceOverviewCard
            description={`The time it takes for your app to start from a cold
                start, which is when a user launches the app and doesn’t have a
                previous session running in the background. This is broken down
                by '${ATTRIBUTE_NAME_MAP[attributeGroups.StartupLatencyCold]}'.`}
            disabled={metricStartupLatencyCold.isEmpty}
            metric="StartupLatencyCold"
          >
            <PercentileLineChart
              metrics={metricStartupLatencyCold.metrics}
              isFetched={metricStartupLatencyCold.isFetched}
              isFetching={metricStartupLatencyCold.isFetching}
              isError={metricStartupLatencyCold.isError}
              refetch={metricStartupLatencyCold.refetch}
              height={160}
              metric="StartupLatencyCold"
            />
          </PerformanceOverviewCard>

          <PerformanceOverviewCard
            description={`The time it takes for your app to start from a warm
                start, which is when the user launches the app which is already
                running in the background and all the system does is bring your
                app to the foreground. This is broken down by '${
                  ATTRIBUTE_NAME_MAP[attributeGroups.StartupLatencyHot]
                }'`}
            disabled={metricStartupLatencyHot.isEmpty}
            metric="StartupLatencyHot"
          >
            <PercentileLineChart
              metrics={metricStartupLatencyHot.metrics}
              isFetched={metricStartupLatencyHot.isFetched}
              isFetching={metricStartupLatencyHot.isFetching}
              isError={metricStartupLatencyHot.isError}
              refetch={metricStartupLatencyHot.refetch}
              height={160}
              metric="StartupLatencyHot"
            />
          </PerformanceOverviewCard>

          <PerformanceOverviewCard
            description={`The average time it takes for requests to complete
                within your app, broken down by '${
                  ATTRIBUTE_NAME_MAP[attributeGroups.NetworkRequestLatency]
                }'.`}
            disabled={metricNetworkRequestLatency.isEmpty}
            metric="NetworkRequestLatency"
          >
            <PercentileLineChart
              metrics={metricNetworkRequestLatency.metrics}
              isFetched={metricNetworkRequestLatency.isFetched}
              isFetching={metricNetworkRequestLatency.isFetching}
              isError={metricNetworkRequestLatency.isError}
              refetch={metricNetworkRequestLatency.refetch}
              height={160}
              metric="NetworkRequestLatency"
            />
          </PerformanceOverviewCard>
        </Grid>

        <PerformanceOverviewCard
          description={`How often your app is generating network requests,
              broken down by '${ATTRIBUTE_NAME_MAP[attributeGroups.NetworkRequestTotal]}'.`}
          disabled={metricNetworkRequestTotal.isEmpty}
          metric="NetworkRequestTotal"
        >
          <PercentileStackedBarChart
            metrics={metricNetworkRequestTotal.metrics}
            isFetched={metricNetworkRequestTotal.isFetched}
            isFetching={metricNetworkRequestTotal.isFetching}
            isError={metricNetworkRequestTotal.isError}
            refetch={metricNetworkRequestTotal.refetch}
            height={160}
            metric="NetworkRequestTotal"
          />
        </PerformanceOverviewCard>

        <Grid
          gap="x8"
          margin="x8"
          repeat={
            match({
              '800px': 2,
            }) || 1
          }
          repeatWidth="1fr"
        >
          <PerformanceOverviewCard
            description={`How much CPU resource your app consumes on the device.
                Here we are plotting the minimum, maximum and median (average)
                usage, broken down by '${ATTRIBUTE_NAME_MAP[attributeGroups.CpuPct]}'.`}
            disabled={metricCpuPct.isEmpty}
            metric="CpuPct"
          >
            <PercentileMinMaxChart
              metrics={metricCpuPct.metrics}
              isFetched={metricCpuPct.isFetched}
              isFetching={metricCpuPct.isFetching}
              isError={metricCpuPct.isError}
              refetch={metricCpuPct.refetch}
              metric="CpuPct"
            />
          </PerformanceOverviewCard>

          <PerformanceOverviewCard
            description={`How much memory resource your app consumes on the
                device. Here we are plotting the minimum, maximum and median (average)
                usage, broken down by '${ATTRIBUTE_NAME_MAP[attributeGroups.MemoryBytes]}'`}
            disabled={metricMemoryBytes.isEmpty}
            metric="MemoryBytes"
          >
            <PercentileMinMaxChart
              metrics={metricMemoryBytes.metrics}
              isFetched={metricMemoryBytes.isFetched}
              isFetching={metricMemoryBytes.isFetching}
              isError={metricMemoryBytes.isError}
              refetch={metricMemoryBytes.refetch}
              metric="MemoryBytes"
            />
          </PerformanceOverviewCard>
        </Grid>
      </PageBody>
    </Page>
  );
};

export default Overview;
